import type { RecommendationDto } from '@ederest/contracts';
import { Badge } from '../../shared/ui/Badge.js';
import { FrictionScore } from '../../shared/ui/FrictionScore.js';
import { MiniBar } from '../../shared/ui/MiniBar.js';
import { Table, type Column } from '../../shared/ui/Table.js';
import {
  COMPONENT_LABEL,
  PLATFORM_CAUSE_LABEL,
  RECOMMENDATION_STATE_LABEL,
  formatDateTime,
  missingLanguages,
} from '../../shared/guidance/labels.js';
import { PRIORITY_HINT, isPriorityKnown, targetOf } from './recommendation-reading.js';

interface RecommendationQueueProps {
  items: readonly RecommendationDto[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** Beside the open detail: the decision and the date are already in the detail, the place goes to the target. */
  compact?: boolean;
}

const DETAIL_COLUMNS = new Set(['state', 'date']);

/** What each column measures, shown on hover of its header and in the legend under the table. */
export const QUEUE_COLUMN_HINTS = {
  priority: PRIORITY_HINT,
  friction:
    "Difficulté mesurée sur l'étape ou le champ, de 0 à 100 : hésitations, erreurs, clics répétés et abandons. Faible sous 40, à surveiller de 40 à 69, difficile à partir de 70.",
  users: "Nombre d'usagers qui ont rencontré cette difficulté sur la période analysée.",
  answer:
    "Ce que l'intelligence artificielle propose : une aide à afficher sur la page, ou une modification du site à demander à l'équipe du portail.",
} as const;

/** The queue as a table: priority first, then the figures, the target and the suggested answer. */
export function RecommendationQueue({
  items,
  selectedId,
  onSelect,
  compact = false,
}: RecommendationQueueProps) {
  const columns: Array<Column<RecommendationDto>> = [
    {
      key: 'priority',
      header: 'Priorité',
      width: '110px',
      hint: QUEUE_COLUMN_HINTS.priority,
      render: (row) =>
        isPriorityKnown(row) ? (
          <div className="cell-priority">
            <MiniBar value={row.priorityScore / 100} tone={row.priorityScore >= 50 ? 'negative' : 'brand'} />
            <span>{row.priorityScore}</span>
          </div>
        ) : (
          <span
            className="cell-muted"
            title="Aucun avis négatif d'usager sur cette page : priorité non calculée."
          >
            Sans avis
          </span>
        ),
    },
    {
      key: 'friction',
      header: 'Friction',
      hint: QUEUE_COLUMN_HINTS.friction,
      render: (row) => <FrictionScore score={row.evidence.frictionScore} />,
    },
    {
      key: 'users',
      header: 'Usagers',
      align: 'end',
      hint: QUEUE_COLUMN_HINTS.users,
      render: (row) => row.evidence.affectedSessions.toLocaleString('fr-FR'),
    },
    {
      key: 'target',
      header: 'Où',
      render: (row) => (
        <div className="cell-target">
          <div className="cell-strong">
            {row.journeyName} › {row.stepName}
          </div>
          <div className="cell-sub">{targetOf(row)}</div>
        </div>
      ),
    },
    {
      key: 'answer',
      header: 'Réponse proposée',
      hint: QUEUE_COLUMN_HINTS.answer,
      render: (row) =>
        row.kind === 'platform_change' ? (
          <span className="cell-badges">
            <Badge tone="teal">Modification du site</Badge>
            {row.platformCause && <span className="cell-sub">{PLATFORM_CAUSE_LABEL[row.platformCause]}</span>}
          </span>
        ) : (
          <span className="cell-badges">
            <span>{row.guidance ? COMPONENT_LABEL[row.guidance.component] : 'Aide sur la page'}</span>
            {row.contentGenerationSkipped &&
              row.guidance &&
              missingLanguages(row.guidance, Object.keys(row.guidance.contents)).length > 0 && (
                <Badge tone="warning">Sans texte rédigé</Badge>
              )}
          </span>
        ),
    },
    {
      key: 'state',
      header: 'Décision',
      render: (row) => {
        const label = RECOMMENDATION_STATE_LABEL[row.state];
        return <Badge tone={label.tone}>{label.text}</Badge>;
      },
    },
    { key: 'date', header: 'Proposée le', render: (row) => formatDateTime(row.createdAt) },
  ];

  return (
    <div data-testid="recommendation-queue" data-selected={selectedId ?? ''}>
      <Table
        columns={compact ? columns.filter((column) => !DETAIL_COLUMNS.has(column.key)) : columns}
        rows={items}
        rowKey={(row) => row.id}
        onRowClick={(row) => onSelect(row.id)}
        empty="Aucune recommandation pour ces critères. De nouvelles propositions arrivent après chaque analyse, dès que la mesure est suffisante."
      />
    </div>
  );
}
